import { ApolloClient, gql } from '@apollo/client';
import { cookieService } from '@bfa/nextjs-common/services';
import { NextPageContext } from 'next';

import { GQLLayoutContent, GQLMyData, GQLQuery, Maybe } from '../gql.generated';
import CookieName from './cookies/cookieName';

type Discount = {
  amount: number;
  isPercentage: boolean;
};

export type CouponConfig = {
  code?: string;
  label?: string;
  supportedCampaigns?: string[];
  programs?: Record<string, string>;
  discount?: Discount;
  isEnabled?: boolean;
  startDate?: Date;
  endDate?: Date;
  expirationPolicy?: Date;
  couponBanner?: any;
};

export type Coupon = {
  isRedeemable: boolean;
  code?: string;
  couponBanner?: any;
};

interface CouponExtendedLayout extends GQLLayoutContent {
  featureFlag: GQLLayoutContent['globalVariable'];
  bannerFlag: GQLLayoutContent['globalVariable'];
  allCouponsConfig: GQLLayoutContent['globalVariable'];
}

interface CouponExtendedMyData extends GQLMyData {
  layout: CouponExtendedLayout;
}

interface ExtendedGQLQuery extends GQLQuery {
  me: CouponExtendedMyData;
}

export const GET_COUPON_CONFIG = gql`
  query getCouponConfig {
    me {
      layout {
        featureFlag: globalVariable(key: "PUBLIC_ACQ_SUBS_COUPONS_ON")
        bannerFlag: globalVariable(key: "PUBLIC_ACQ_SUBS_COUPONS_BANNER_ON")
        allCouponsConfig: globalVariable(key: "PUBLIC_ACQ_SUBS_COUPONS_CONFIG")
      }
    }
  }
`;

const getCouponByCid = (coupons: Record<string, CouponConfig>, cid: string) => {
  if (!cid) return undefined;
  const lowerCaseCID = cid.toLowerCase();
  return Object.entries(coupons).find((coupon) =>
    coupon[1]?.supportedCampaigns?.some((campaign) =>
      lowerCaseCID.includes(campaign.toLowerCase())
    )
  )?.[1];
};

export const getCouponConfig = async (
  client: ApolloClient<unknown>,
  ctx: Maybe<NextPageContext>
): Promise<Coupon> => {
  const { data, error } = await client.query<ExtendedGQLQuery>({
    query: GET_COUPON_CONFIG,
  });

  if (error) {
    return { isRedeemable: false };
  }

  const ipcurCookie = cookieService.getCookieAsJson(ctx, CookieName.IPCUR);
  const { cid } = ipcurCookie;
  const isFeatureEnable: boolean =
    data?.me?.layout?.featureFlag?.toLowerCase() === 'true' || false;
  const isBannerEnabled: boolean =
    data?.me?.layout?.bannerFlag?.toLowerCase() === 'true' || false;
  const allCouponsConfig: Record<string, CouponConfig> =
    (data?.me?.layout?.allCouponsConfig &&
      JSON.parse(data?.me?.layout?.allCouponsConfig)) ||
    {};
  const couponConfig = getCouponByCid(allCouponsConfig, cid);
  const activationTime = Date.now();
  const isRedeemable = !!(
    isFeatureEnable &&
    couponConfig?.isEnabled &&
    activationTime >= new Date(couponConfig?.startDate || '').getTime() &&
    activationTime <= new Date(couponConfig?.endDate || '').getTime()
  );
  const couponBanner =
    isBannerEnabled && couponConfig?.code && couponConfig?.couponBanner
      ? couponConfig.couponBanner
      : undefined;

  return {
    isRedeemable,
    code: couponConfig?.code,
    couponBanner,
  };
};
